import React from 'react';
import PartnerPageTemplate from './PartnerPageTemplate';
import {
  Users,
  Phone,
  ClipboardList,
  Repeat,
  HeartPulse,
  FileCheck,
  ShieldCheck,
  MapPin,
  Clock,
  Briefcase,
} from 'lucide-react';

const CANONICAL = 'https://www.moveracaremobility.com/healthcare-partners/case-managers';

const CaseManagers = () => {
  const seo = {
    title: 'Case Manager Transportation Resource | NEMT for Care Coordinators | MoveraCare',
    description: 'A direct non-emergency medical transportation resource for case managers, adjusters, care coordinators, and social workers coordinating patient transportation across Greater Houston.',
    canonical: CANONICAL,
    schemaId: 'partner-case-managers-schema',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'NEMT for Case Managers & Care Coordinators',
      serviceType: 'Non-Emergency Medical Transportation',
      areaServed: 'Greater Houston, TX',
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        telephone: '+1-832-925-2128',
        url: 'https://www.moveracaremobility.com',
      },
      audience: {
        '@type': 'MedicalAudience',
        audienceType: 'Case Managers, Care Coordinators, Adjusters, Social Workers',
      },
    },
  };

  return (
    <PartnerPageTemplate
      testid="partner-case-managers"
      seo={seo}
      eyebrow="CASE MANAGERS & COORDINATORS"
      heading="A Direct Transportation Resource for Case Managers &amp; Care Coordinators"
      intro="MoveraCare Mobility is a Greater Houston non-emergency medical transportation provider prepared to work directly with case managers, adjusters, care coordinators, and social workers coordinating patient transportation for medical, therapy, and recovery destinations."
      audience={{
        title: 'For Professionals Coordinating Patient Care & Transportation',
        description: 'This page is for case managers and coordinators whose day-to-day work includes arranging non-emergency medical transportation for patients — whether from home to an appointment, between facilities, or through a treatment plan.',
        bullets: [
          'Hospital and outpatient case managers',
          'Workers’ Compensation and insurance case managers',
          'Nurse case managers and clinical care coordinators',
          'Discharge planners and social workers',
          'Rehabilitation and therapy care coordinators',
          'Community-based case management programs',
        ],
      }}
      problem={{
        title: 'One Reliable Transportation Contact, Every Time',
        description: 'Coordinators are juggling authorizations, appointments, and family communication. MoveraCare aims to be the transportation call you don’t have to worry about — a single provider that shows up, communicates, and documents.',
        bullets: [
          'Direct dispatch access without navigating multiple vendors',
          'Wheelchair and ambulatory options based on patient mobility',
          'Recurring transportation for treatment schedules and follow-up',
          'Trip documentation supporting authorization and billing workflows',
          'Clear communication when scheduling changes or issues arise',
        ],
      }}
      capabilities={{
        title: 'Transportation Coordination Support',
        items: [
          { icon: ClipboardList, title: 'Direct Trip Requests', desc: 'Submit patient transportation requests directly through the Request Patient Transportation form or dispatch line.' },
          { icon: Repeat, title: 'Recurring Care Support', desc: 'Standing schedules for dialysis, therapy, rehabilitation, and other recurring treatment plans.' },
          { icon: HeartPulse, title: 'Wheelchair & Ambulatory', desc: 'Wheelchair-accessible vans and comfortable ambulatory transport; drivers assist passengers during transport.' },
          { icon: Briefcase, title: 'Workers’ Compensation Transport', desc: 'Support for authorized transportation to Workers’ Comp-approved medical and therapy destinations.' },
          { icon: FileCheck, title: 'Trip Documentation', desc: 'Trip completion records to support authorization, care plans, and payer workflows.' },
          { icon: Phone, title: 'Coordinator-Friendly Communication', desc: 'Clear, timely communication with case managers when scheduling adjustments are needed.' },
        ],
      }}
      coordination={{
        title: 'How Case Managers Work With MoveraCare',
        steps: [
          { title: 'Trip Request', desc: 'Case manager submits patient details, mobility level, destination, and any authorization or payer info.' },
          { title: 'Confirmation', desc: 'MoveraCare confirms vehicle type, pickup window, and communicates the plan back to the coordinator.' },
          { title: 'Transportation Completed', desc: 'A wheelchair or ambulatory driver completes the trip. Door-to-door or curb-to-curb assistance is coordinated at booking.' },
          { title: 'Documentation Returned', desc: 'Trip completion documentation is available to support case notes, authorizations, and billing.' },
        ],
      }}
      why={{
        title: 'Why Case Managers Consider MoveraCare',
        items: [
          { icon: Phone, title: 'Direct Dispatch Access', desc: 'A single contact for scheduling, adjustments, and trip questions — no vendor maze.' },
          { icon: Users, title: 'Patient-Centered Drivers', desc: 'Drivers who assist passengers during transport, focused on dignity and clear communication.' },
          { icon: Repeat, title: 'Recurring Coordination', desc: 'Reliable standing schedules for ongoing treatment and recovery plans.' },
          { icon: FileCheck, title: 'Documentation That Supports Care Plans', desc: 'Trip records that align with authorization, case notes, and billing workflows.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious Handling', desc: 'Professional handling of patient information across dispatch and operations.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Verified service area across Fort Bend and the surrounding counties.' },
        ],
      }}
      transportRequest={{
        title: 'Submit a Patient Transportation Request',
        description: 'Case managers and care coordinators can submit non-emergency transportation requests directly through MoveraCare’s intake form. Same-day and recurring requests are both supported.',
        cta: { label: 'Request Patient Transportation', to: '/request-patient-transportation' },
      }}
      vendorRelationship={{
        title: 'Set Up an Organizational Relationship',
        description: 'For case management organizations, employers, and payers ready to formalize a working relationship with MoveraCare, download the vendor procurement packet or contact Provider Relations.',
      }}
    />
  );
};

export default CaseManagers;
